import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full paragraph-base transition-all  disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        default:
          "bg-brand text-white hover:bg-brand/80 button-branded",
        primary: 
          "bg-brand text-white hover:bg-brand/80 button-branded",
        secondary:
          "bg-secondary text-primary shadow-xs hover:bg-primary hover:text-primary-foreground",
        outline:
          "border-2 border-primary text-primary bg-transparent hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        // link: "text-primary !font-normal underline-offset-4 hover:underline hover:text-red-500",
        link: "text-brand !font-bold !p-0 gap-1 leading-[20px] hover:gap-2",
        'link-reserve': "text-white !p-0 gap-1 leading-[20px] !font-light uppercase",
        'link-reserve-dark': "text-grey-900 !p-0 gap-1 leading-[20px] !font-light uppercase",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
      },
      size: {
        default: "h-9 px-2 py-2 [&_.material-symbols-outlined]:!text-[22px] [&_.material-symbols-outlined]:!h-5 [&_.material-symbols-outlined]:!w-5",
        sm: "h-9 px-4 py-2 [&_.material-symbols-outlined]:!text-[20px] [&_.material-symbols-outlined]:!h-5 [&_.material-symbols-outlined]:!w-5",
        lg: "h-11 px-6 py-2 [&_.material-symbols-outlined]:!text-[20px] [&_.material-symbols-outlined]:!h-5 [&_.material-symbols-outlined]:!w-5",
        icon: "size-10 p-2 flex items-center justify-center cursor-pointer",
        iconSm: "size-10 p-2 flex items-center justify-center cursor-pointer",
        iconLg: "size-11 p-2 flex items-center justify-center cursor-pointer",
        // link: "h-11 px-0 py-2 [&_.material-symbols-outlined]:!text-[20px] [&_.material-symbols-outlined]:!h-5 [&_.material-symbols-outlined]:!w-5",
      },
      lifted: {
        true: "shadow-sm hover:shadow-md",
        false: "",
      },
      grayBorder: {
        true: "!border-border",
        false: "",
      },
      fontNormal: {
        true: "font-normal",
        false: "font-bold",
      },
      linkWithoutArrow: {
        true: "!gap-1",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      lifted: false,
      grayBorder: false,
    },
  }
)

function Button({
  className,
  variant,
  size,
  lifted = false,
  grayBorder = false,
  asChild = false,
  fontNormal = false,
  linkWithoutArrow = false,
  children,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    lifted?: boolean
    grayBorder?: boolean
    fontNormal?: boolean
    linkWithoutArrow?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className, lifted, grayBorder, fontNormal, linkWithoutArrow }))}
      {...props}
    >
      {variant === "link" && !linkWithoutArrow ? (
        <span className="flex items-center gap-1">
          {children}
          <span className="material-symbols-outlined !text-[20px] !h-5 !w-5">arrow_forward</span>
        </span>
      ) : variant === "link-reserve" || variant === "link-reserve-dark" ? (
        <span className="group/rb flex items-center gap-2">
          {children}
          <span className={`material-symbols-outlined text-[20px]! h-5! w-5! inline-block transition-transform duration-300 ease-in-out group-hover/rb:translate-x-1.5 ${variant === "link-reserve-dark" ? "!text-grey-700" : "!text-white/80"}`}>arrow_forward</span>
        </span>
      ) : (
        children
      )}
    </Comp>
  )
}

export { Button, buttonVariants }
